
import { computed, defineComponent, onActivated, onMounted, reactive, ref, toRefs, watch, onUnmounted } from 'vue'
import ProductInformation from '@/pages/Products/ProductDetail/components/ProductInformation.vue'
import ProductSearchModal from '@/pages/Products/components/ProductSearchModal.vue'
import useContentModule, { Mutation } from '@/pages/composables/useContentModule'
import { CtAsset, CtProduct, CtProp, CtStockItem } from '@/models/interfaces/content-type'
import { useStore } from 'vuex'
import Directr, { ContentType, DirectrEvent } from '@/services/directr'
import ChannelInformation from '@/pages/Products/ProductDetail/components/ChannelInformation.vue'
import StockInformation from '@/pages/Products/ProductDetail/components/StockInformation.vue'
import DButton from '@/components/base/DButton.vue'
import TrashIcon from '@/components/Icons/TrashIcon.vue'
import { useRoute, useRouter } from 'vue-router'
import CloseIcon from '@/components/Icons/CloseIcon.vue'
import OptionsAndVariants from '@/pages/Products/ProductDetail/components/OptionsAndVariants.vue'
import { useQuasar } from 'quasar'
import _ from 'lodash'
import ListHeader from '@/components/ListHeader.vue'
import PenIcon from '@/components/Icons/PenIcon.vue'
import ChevronRightIcon from '@/components/Icons/ChevronRightIcon.vue'
import ChevronLeftIcon from '@/components/Icons/ChevronLeftIcon.vue'
import ProductCard from '@/pages/Products/components/ProductCard.vue'
import { format } from '@/utils/template'

import $icecat from '@/services/icecat'
import { UUID } from '@Protium/vue-ozone-utils'
import { $settle } from '@/utils/promise'
import { errorToReadableString } from '@/utils/error'
import { MetricLengthUnit, MetricWeightUnit } from '@/models/generics'
import { STOCK_ITEM_ARTICLE_NUMBER_BASE } from '@/constants'
import SKU from '@/utils/sku'
import { $padString, PadPosition } from '@/utils'

export interface ResolvedCtProduct extends Omit<CtProduct, 'components' | 'variants'> {
  is_parent?: boolean,
  components: Array<CtStockItem>,
  variants: Array<ResolvedCtProduct> | null
}

export default defineComponent({
  name: 'ProductDetailIndex',
  components: {
    ProductSearchModal,
    ProductCard,
    ChevronRightIcon,
    ChevronLeftIcon,
    PenIcon,
    ListHeader,
    OptionsAndVariants,
    CloseIcon,
    TrashIcon,
    DButton,
    StockInformation,
    ChannelInformation,
    ProductInformation
  },

  props: {
    productId: {
      type: String,
      required: true,
    },
    creating: {
      type: String,
      default: 'false',
    }
  },

  setup (props) {
    const $store = useStore()
    const $router = useRouter()
    const $route = useRoute()
    const $directr: Directr = $store.getters['Services/Directr']
    const $q = useQuasar()
    const channelRoute = ref({})

    const isCreating = ref(props.creating === 'true')

    onMounted(() => {
      let updateNotify
      $directr.listen(DirectrEvent['Update/Product'], (payload) => {
        if (!state.product) state.product = payload
        else if (payload?.sys?.id
          && state.product.sys.updated_at
          && (new Date(payload.sys.updated_at).valueOf() > new Date(state.product.sys.updated_at).valueOf())
        ) state.product = { ...state.product, ...payload }
        if (!updateNotify) updateNotify = $q.notify({ message: 'Product updated', group: false, type: 'positive', classes: 'custom-notify success' })
        else updateNotify({ message: 'Product updated', type: 'positive', group: false, classes: 'custom-notify success' })
      })
    })

    const productDraft = reactive({
      sys: { id: props.productId, },
      images: [],
      title: '',
      description: '',
      ean: [],
      sku: '',
      channels: [],
      components: [],
      options: [],
    })

    const state = reactive({
      loading: true,
      product: null as ResolvedCtProduct | null,
      channels: null,
      components: [] as Array<CtStockItem>,
      stockItems: null,
      allProps: [] as Array<CtProp> | undefined,
      showDeleteDialog: false,
      deleteCascade: {
        catalogue_products: false,
        stock_items: false,
        variants: false
      },
      parentProduct: null as ResolvedCtProduct | null
    })

    const iceCatState = reactive({
      loading: false,
      show: null, // show icecat notification
      showDialog: false, // show icecat datasheet with apply options
      dialogTarget: null, // set to the key of iceCatState.data to display
      data: new Map(),
      applyTo: new Map()
    })
    const hasIceCatData = computed(() => iceCatState.data.size >= 1)

    async function queryIcecat (ean, targetIndex?: null | number) {
      if (!ean) return
      const target = !targetIndex ? state.product : state.components[targetIndex]

      try {
        iceCatState.loading = true
        const { fulfilled: products, rejected } = await $settle([$icecat.product.byEan(ean)])

        if (rejected?.length && !products?.length) return

        const product = products[0]
        if (Object.keys(product || {}).length && target) {
          // console.log('$icecat.product.extract.dimensions(product)', $icecat.product.extract.dimensions(product))
          iceCatState.data.set(target?.sys.id, $icecat.product.extract.all(product))
          iceCatState.applyTo.set(target?.sys.id, [])

          // iceCatState.show = true
        }
        // else iceCatState.show = false

      } catch (e) {
        console.warn('Icecat query failed', e)
      } finally {
        iceCatState.loading = false
      }
    }

    watch(() => state.product?.ean, queryIcecat as any)
    watch(() => state.components, value => {
      value?.map((c, i) => queryIcecat(c.ean?.[0], i))
    })

    function enrichItem (id?: UUID) {
      const items = !id ? [...iceCatState.data.keys()] : [id]
      if (!items?.length) return
      if (!id) items.forEach(itemId => {
        iceCatState.applyTo.set(itemId, $icecat.product.keys())
      })

      items.forEach(itemId => {
        const data = iceCatState.data.get(itemId)
        const applied = {}
        const item = itemId === props.productId ? state.product : state.components?.find(c => c.sys.id === itemId)
        if (!item) return
        // selectively apply extracted data

        iceCatState.applyTo.get(itemId).forEach(key => {
          //TODO: if key is a relation (i.e categories), check for existence first..., and get the UUID

          if (Object.keys(item).includes(key)) {
            item[key] = data[key]
            applied[key] = data[key]
          }
        })

        // clear the state for the enriched item
        iceCatState.data.delete(itemId)
        iceCatState.applyTo.delete(itemId)

        if (itemId === props.productId) saveProduct(itemId, applied).catch(console.error)
        else saveComponent(itemId, applied).catch(console.error)
        $q.notify({ message: 'Icecat data applied!', classes: 'custom-notify success', position: 'top' })
      })

      if (!iceCatState.data?.size) iceCatState.showDialog = false
    }

    const duplicateProduct = reactive({
      showDuplicateDialog: false,
      duplicateValue: '',
      duplicateProduct: null as CtProduct | null
    })

    const getParentProduct = async (productId) => {
      const { items } = await $directr.get('product', [
        { field: 'variants', method: 'in', match: productId, type: 'uuid' },
      ])

      if (!items?.length) {
        state.parentProduct = null
        return
      }

      state.parentProduct = items[0]
    }

    const getDuplicateProduct = async (sku) => {
      const { items } = await $directr.get('product', [
        { field: 'sku', method: 'like', match: sku },
      ], { query: { all: true } })
      duplicateProduct.duplicateProduct = items?.[0]
    }

    const editDuplicateProduct = () => {
      isCreating.value = false
      if (duplicateProduct?.duplicateProduct?.sys?.id) {
        void $router.push({
          name: 'ProductDetailIndex',
          params: { productId: duplicateProduct.duplicateProduct.sys.id },
        })
      }
    }

    const editCurrentProduct = () => {
      isCreating.value = true
    }

    const {
      requestHandler,
      contentType
    } = useContentModule<CtProduct>('Products', $store)

    const getProduct = async (productId) => {
      try {
        const product = await requestHandler.get(ContentType.Product, productId, { resolve: 0 })
        state.loading = true
        if (product.items.length) {
          state.product = product.items[0]
          const { items: components } = state.product?.components?.length
            ? await requestHandler.get(ContentType.StockItem, state.product?.components, { resolve: 1 })
            : { items: [] }
          const { items: variants } = state.product?.variants?.length
            ? await requestHandler.get(ContentType.Product, state.product?.variants, { resolve: 1 })
            : { items: [] }
          const { items: options } = state.product?.options?.length
            ? await requestHandler.get(ContentType.Prop, state.product?.options)
            : { items: [] }

          if (state.product) {
            state.product.options = options ?? []
            state.product.variants = variants?.map(v => ({ ...v, components: v?.components ?? [] })) ?? []
            const mappedComponents = [...state.product?.components || []]?.reduce((comps, uuid) => {
              if (!uuid) return comps
              const c = components?.find(f => f.sys.id === uuid) || {}
              comps.push({
              ...c,
              title: c?.title || 'Component not found...',
              categories: c?.categories ?? [],
              packing: c?.packing ?? [],
              props: c?.props ?? {},
              dimensions: c?.dimensions ?? {}
            })

            return comps
            }, [] as typeof state.components) || []
            state.components = mappedComponents
            state.product.components = mappedComponents

            void getParentProduct(state.product.sys.id)
          }
          void getAllProps()
        } else if (!isCreating.value) {
          void $router.push({ name: 'Products' })
        }
      } catch (e) {
        $q.notify({ message: 'Some product-data failed to load, please reload.', type: 'warn', classes: 'custom-notify warn' })
      } finally {
        state.loading = false
      }
    }

    const getAllProps = async () => {
      const { items: props } = await $directr.get(ContentType.Prop, [])
      state.allProps = props
    }

    const saveProduct = async (productSysId, savePayload) => {
      // const method = isCreating.value ? 'post' : 'put'

      if (isCreating.value) savePayload = { ...savePayload, sys: { id: props.productId } }

      // if no sku was set, fetch stock-item sorted by sku, and count up +1
      let defaultSku: string
      if (!savePayload.sku && isCreating.value) {
        const { sys } = await $directr.get('stock', [{ select: ['sku'] }], { order: 'sku', distinct: 'sku', limit: 1 })
        defaultSku = $padString(String((sys.total || 0) + 1), '0', STOCK_ITEM_ARTICLE_NUMBER_BASE.length, PadPosition.Before)
        savePayload.sku = defaultSku
      }

      try {
        await requestHandler.put(contentType, productSysId, savePayload, { resolve: 2 })
      } catch (error) {
        if ('sku' in savePayload && (error as any).data.message.includes('product_sku')) {
          duplicateProduct.showDuplicateDialog = true
          duplicateProduct.duplicateValue = 'SKU'
          void getDuplicateProduct(savePayload.sku)
          return
        }
        console.error(error)
        return
      }

      const finishCreate = async () => {
        try {
          await getProduct(productSysId)
          isCreating.value = false

          // remove creating from parameters
          if ($route.params.creating) {
            const newParams = { ...$route.params }
            delete newParams.creating
            void $router.replace({
              name: 'ProductDetailIndex',
              params: { ...newParams },
            }
            )
          }
        } catch (e) {
          console.error(e)
        }
      }

      // show modal for automatically creating stock item with same title/ean/sku
      if (!$route.params.creating) return finishCreate()

      $q.dialog({
        title: 'Create a new stock item for this product?',
        message: 'We\'ll automatically create a stock-item with the same Title, EAN and SKU',
        cancel: true,
      }).onOk((val) => {
        const componentNotify = $q.notify({ message: 'Creating Stock Item', group: false, spinner: true, timeout: 10000 })
        void $directr.get('stock', [{ select: ['sku'] }], { order: 'sku', distinct: 'sku', limit: 1 })
        .then(({ items: stockItems }) => {
          $directr.put(ContentType.StockItem, [], {
          title: savePayload.title,
          ean: savePayload.ean,
          sku: savePayload.sku,
          weight: 1,
          dimensions: { x: 1, y: 1, z: 1, unit: MetricLengthUnit.Centimeter, weight_unit: MetricWeightUnit.Gram }
        }, { force: true }).then(({ items }) => {
          const component = items?.[0]
          componentNotify({ message: 'Adding component', spinner: true, timeout: 10000 })
          if (component) $directr.put(ContentType.Product, productSysId, { components: [component.sys.id] }).then(() => {
            componentNotify({ message: 'Component added', type: 'positive', classes: 'custom-notify success', spinner: false, timeout: 2000 })
            void finishCreate()
          }).catch((e) => {
            componentNotify({
              message: 'There was an issue while adding the component',
              caption: errorToReadableString(e),
              type: 'negative',
              classes: 'custom-notify error',
              spinner: false,
              timeout: 2000
            })
            void finishCreate()
          })
        }).catch((e) => {
          componentNotify({
              message: 'There was an issue while creating the Stock Item',
              caption: errorToReadableString(e),
              type: 'negative',
              classes: 'custom-notify error',
              spinner: false,
              timeout: 2000
            })
            void finishCreate()
        })
        })
      }).onCancel(() => {
        void finishCreate()
      }).onDismiss(() => {
        void finishCreate()
      })
    }

    const saveComponent = async (componentSysId, savePayload) => {
      try {
        if ('count' in savePayload) {
          // creates a new array of component id's by removing the existing ones and pushing it the amount of times as the count
          let idArray = state.product?.components.map(component => component.sys.id)
          idArray = idArray?.filter(id => id !== componentSysId)
          idArray = idArray?.concat(Array(savePayload.count).fill(componentSysId))
          const customSavePayload = { 'components': idArray }

          await saveProduct(props.productId, customSavePayload)
          delete savePayload.count
        }

        await requestHandler.put(ContentType.StockItem, componentSysId, savePayload)
      } catch (e) {
        $q.notify({ type: 'negative', message: 'Error saving product component', caption: errorToReadableString(e), classes: 'custom-notify error' })
      }
      await getProduct(props.productId)
    }

    const addProductComponents = async (savePayload) => {
      if (!state.components) state.components = []
      const currComponents = state.components.map(component => component.sys.id)
      const componentArr = currComponents.concat(savePayload)
      await requestHandler.put(contentType, props.productId, { components: componentArr })
      await getProduct(props.productId)
    }

    const removeProductComponent = async (savePayload) => {
      if (!state.components) state.components = []
      const componentArr = state.components.map(component => component.sys.id).filter(id => id !== savePayload)
      await requestHandler.put(contentType, props.productId, { components: componentArr })
      await getProduct(props.productId)
    }

    const saveProductOptionsAndVariants = async (options, variants, variantsToDelete) => {
      if (!state.product) return
      const newOptions = options.filter(o => o.sys.draft)
      if (newOptions.length) {
        await Promise.all([
          ...newOptions
            .map(o => ({ ...o, id: _.kebabCase(o.name) }))
            .map(async o => await requestHandler.put(ContentType.Prop, o.sys.id, o)),
        ])
      }
      if (state.product?.components?.length) {
        const newOptionProps = options.filter(o => !(o.sys.id in (state.product?.components[0]?.props ?? {})))
        if (newOptionProps.length) {
          state.product.components[0] = await requestHandler.put(ContentType.StockItem, state.product.components[0]?.sys?.id, {
            props: {
              ...(state.product.components[0]?.props ?? {}),
              ...newOptionProps.reduce((prev, nop) => ({ ...prev, [nop.sys.id]: '' }), {})
            }
          }).then(({ items = [] }) => items[0])
        }
      }
      if (variants?.length) {
        variants = variants.map(v => ({
          ...v,
          components: v.components.map((c, i) => ({
            ...c,
            props: {
              ...c.props,
              ...(i === 0 ? options.filter(o => !(o.sys.id in (c?.props ?? {}))).reduce((prev, o) => ({
                ...prev,
                [o.sys.id]: ''
              }), {}) : {})
            }
          }))
        }))
        await Promise.all([
          // save draft variants and stock items
          ...variants.filter(v => v.sys.draft)
            .flatMap(v => v.components)
            .map(si => ({
              ...si,
              categories: si.categories?.map(c => c.sys.id) ?? [],
              packing: si.packing?.map(p => p.sys.id) ?? [],
            }))
            .map(async si => await requestHandler.put(ContentType.StockItem, si.sys.id, si)),

          ...variants.filter(v => v.sys.draft)
            .map(async v => await requestHandler.put(ContentType.Product, v.sys.id, {
              ...v,
              categories: (v.categories ?? []).map(c => c.sys.id) ?? [],
              components: (v.components ?? []).map(c => c.sys.id) ?? [],
              options: (v.options ?? []).map(o => o.sys.id) ?? [],
            })),

          // save modified stock items for existing variants
          ...variants.filter(v => !v.sys.draft)
            .flatMap(v => v.components.slice(0, 1))
            .map(si => ({
              ...si,
              categories: si.categories?.map(c => c.sys.id) ?? [],
              packing: si.packing?.map(p => p.sys.id) ?? [],
            }))
            .map(async si => await requestHandler.put(ContentType.StockItem, si.sys.id, {
              props: si.props,
            })),
          ...variants.filter(v => !v.sys.draft).map(async v => (
            await requestHandler.put(ContentType.Product, v.sys.id, {
              title: v.title
            })
          ))
        ])
      }
      await requestHandler.put(ContentType.Product, props.productId, {
        options: (options ?? []).map(o => o.sys.id),
        variants: (variants ?? []).map(v => v.sys.id)
      })

      await Promise.all(variantsToDelete.map(async v => await $directr.product.deleteCascade(v,)))

      await getProduct(props.productId)
    }

    const getChannelRoute = () => {
      const route = localStorage.getItem('channelRoute')
      const channelId = localStorage.getItem('channelId')
      channelRoute.value = { route: route, channelId: channelId }
    }

    onMounted(async () => {
      if ($route.params.creating == 'true') {
        isCreating.value = true
      }
      await getProduct(props.productId)
      getChannelRoute()
    })

    onUnmounted(() => {
      localStorage.removeItem('channelRoute')
      localStorage.removeItem('channelId')
    })

    watch(() => props.productId, async () => await getProduct(props.productId))

    const onDelete = async () => {
      if (!state.product) return
      await $directr.product.deleteCascade(props.productId, state.deleteCascade)
      state.showDeleteDialog = false
      $q.notify({
        message: 'Product succesfully deleted',
        position: 'bottom',
        type: 'positive',
        classes: 'custom-notify product-notify success'
      })
      void $router.push({ name: 'Products' })
    }

    const optionsAndVariants = ref()

    $router.beforeEach((to, from, next) => {
      localStorage.setItem('channelRoute', from?.name as string)
      localStorage.setItem('channelId', from.params?.channelId as string)
      next()
    })

    return {
      ...toRefs(state),
      ...toRefs(duplicateProduct),
      iceCatState,
      hasIceCatData,
      enrichItem,
      editDuplicateProduct,
      editCurrentProduct,
      productDraft,
      saveProduct,
      saveComponent,
      addProductComponents,
      removeProductComponent,
      saveProductOptionsAndVariants,
      onDelete,
      isCreating,
      optionsAndVariants,
      format,
      channelRoute
    }
  }
})
